import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import type React from 'react';

interface BrandIdentityProps {
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
  secondaryColor: string;
  setSecondaryColor: (color: string) => void;
  fontFamily: string;
  setFontFamily: (font: string) => void;
  logoStoragePath: string;
  logoUrl: string | null;
  isUploadingLogo: boolean;
  handleLogoUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const BrandIdentity: React.FC<BrandIdentityProps> = ({
  primaryColor,
  setPrimaryColor,
  secondaryColor,
  setSecondaryColor,
  fontFamily,
  setFontFamily,
  logoStoragePath,
  logoUrl,
  isUploadingLogo,
  handleLogoUpload,
}) => {
  return (
    <>
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 4 }}>
        Brand Identity
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Primary Brand Color (Hex)"
            variant="outlined"
            fullWidth
            value={primaryColor}
            onChange={(e) => setPrimaryColor(e.target.value)}
            placeholder="#FFFFFF"
            slotProps={{
              input: {
                startAdornment: (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 1,
                      bgcolor: primaryColor || 'transparent',
                      border: '1px solid #ccc',
                      mr: 1,
                    }}
                  />
                ),
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Secondary Brand Color (Hex)"
            variant="outlined"
            fullWidth
            value={secondaryColor}
            onChange={(e) => setSecondaryColor(e.target.value)}
            placeholder="#000000"
            slotProps={{
              input: {
                startAdornment: (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 1,
                      bgcolor: secondaryColor || 'transparent',
                      border: '1px solid #ccc',
                      mr: 1,
                    }}
                  />
                ),
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            freeSolo
            options={[
              'Arial',
              'Helvetica',
              'Times New Roman',
              'Courier New',
              'Roboto',
              'Open Sans',
              'Lato',
              'Montserrat',
              'Inter',
              'Poppins',
            ]}
            value={fontFamily}
            onInputChange={(_, newValue) => setFontFamily(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Brand Font Family" variant="outlined" fullWidth />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, height: '100%' }}>
            <Button variant="outlined" component="label" disabled={isUploadingLogo}>
              {isUploadingLogo ? <CircularProgress size={24} /> : 'Upload Logo'}
              <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
            </Button>
            {logoUrl && (
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  border: '1px solid #ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  bgcolor: '#f5f5f5',
                }}
              >
                <img
                  src={logoUrl}
                  alt="Brand Logo Preview"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </Box>
            )}
            {logoStoragePath && !logoUrl && (
              <Typography variant="body2" color="success.main" noWrap>
                Logo uploaded successfully
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
